/**
 * 이상한 방 - 설정 (톱니바퀴 버튼 하나 + 패널)
 *
 * 근거 문서: 상태모델 원칙 2(화면을 가리는 것은 해악)
 *           디자인 시안 A(2026-08-31 "온에어" -> 2026-09-02 "현장 감식".
 *           형태는 그대로이고 은유만 바뀌었다. 규칙의 원본은 `gameHud.css`)
 *
 * ## 왜 버튼 하나로 모았나
 *
 * 직전까지 화면 오른쪽 위에는 언어 국기와 음소거 스피커가 각각 고정 자리를 차지하고 있었다.
 * 거기에 BGM과 진동까지 붙이면 네 개다. **관찰 게임에서 UI가 덮은 면적은 곧 볼 수 없는
 * 방의 넓이**이고(원칙 2), 넷 다 "한 번 정하면 다시 만지지 않는 값"이라 상시 표시의 값이
 * 가장 낮은 종류다. 그래서 상시로 남는 것은 톱니바퀴 하나이고 나머지는 그 안에 있다.
 *
 * ## 자리: 오른쪽 위 한 칸, 모든 화면 위
 *
 * 소리를 끄고 싶어지는 순간은 **화면과 무관하게** 온다 - 파티게임은 방송·공공장소에서 켜지고,
 * 그 순간이 하필 결과 화면이나 단계 안내일 때가 많다. 그래서 겹침 순서에서 가장 위에 서고
 * (HUD 20 < 결과 30 < 선택 40 < 안내 45 < **이것 46**), 화면을 옮겨도 접히지 않는다.
 * 한 화면에서라도 덮이면 이 버튼은 "보이는데 안 눌리는 물건"이 되고, 그건 없는 것보다 나쁘다.
 *
 * 라운드 중에는 **흐려진다**(`setQuiet`). 수색 중 시야 구석에서 노란 아이콘이 빛나고 있으면
 * 그 자체가 방을 훑는 눈을 끌어당긴다. 크기는 그대로 두고 명도만 낮추는 이유는 터치 타깃이다 -
 * 작아지는 버튼은 조준이 어려워지고, 빗맞은 탭은 방을 향한 한 발이 되어 탄환을 먹는다.
 *
 * ## 열려 있는 동안 라운드는 멈춘다
 *
 * 판정·규칙을 건드리지 않고 **프레임 공급만 끊는다**(`main.ts`의 `frame`이 `isSettingsOpen()`을
 * 본다). 오조작으로 열렸을 때 잃는 것이 0이어야 하기 때문이다 - 수색 5분짜리 판에서 설정을
 * 잘못 열어 30초를 잃으면 그 판은 버려진다. 시간이 흐르지 않으므로 스크림이 방을 덮어도
 * 아무것도 손해 보지 않는다.
 *
 * 입력이 뒤로 새지 않는 것도 같은 층에서 막는다. 스크림이 화면 전체를 덮으므로 캔버스는
 * 포인터 이벤트를 아예 받지 못하고(제스처 인식기가 캔버스에 붙어 있다), 키는 창 단위
 * 핸들러들이 `isSettingsOpen()`으로 스스로 비켜 준다.
 *
 * ## 상태를 글자로 쓰지 않는다
 *
 * 개발자 요구가 그것이다 - "켬 끔 이것도 굳이 필요없고 그냥 아이콘으로만". 스피커에 빗금이
 * 그어지면 꺼진 것이고, 그 옆에 "끔"이라고 또 쓰는 것은 같은 말을 두 번 하는 것이다.
 * 대신 상태는 **세 채널**로 나간다: 모양(빗금이 그어지고 물결/파동이 사라진다) · 색(노랑 →
 * 흐린 회색) · 접근성 이름(`aria-label`에 "켜짐/꺼짐"이 문장으로 들어간다).
 * 색만으로 구분하지 않는 이유는 색각 대응이고, 아이콘만으로 끝내지 않는 이유는 낭독기에
 * 아이콘이 보이지 않기 때문이다.
 *
 * ## 상태를 이 위젯이 갖지 않는다
 *
 * 음소거의 원본은 오디오 엔진, 진동은 햅틱 엔진, 언어는 사전 엔진이다. 여기 `muted` 같은
 * 변수를 하나라도 두는 순간 저장된 값과 화면이 갈릴 수 있는 창이 생기고, 그 창은 "껐는데
 * 소리가 난다"로만 드러난다. 그래서 항목은 **묻는 함수와 바꾸는 함수**만 넘겨받는다.
 *
 * ## 항목을 늘리는 방법
 *
 * `items` 배열에 한 줄을 더한다. 그게 전부다 - 이 파일은 항목이 무엇인지 모르고,
 * 아이콘 이름과 "켬/끔인가 값 고르기인가"만 안다.
 */

import { onLocaleChange, t } from '../i18n'

import { createElement, setAttr, setText } from './dom'

import './gameHud.css'

/* --- 항목 계약 ------------------------------------------------------------------ */

/**
 * 아이콘 이름. CSS가 그리므로(에셋 0) 새 아이콘은 `gameHud.css`에 조각을 더해야 한다.
 * 이름이 항목 id가 아니라 **그림**인 이유는 항목 둘이 같은 그림을 쓸 수 있어서다.
 */
export type SettingsIcon = 'sound' | 'bgm' | 'haptics' | 'lang'

type SettingsItemBase = Readonly<{
  /** 진단·테스트용 식별자. 화면에 나오지 않는다. */
  id: string
  /** 항목 이름의 사전 키. 아이콘만으로는 무엇의 스위치인지 모를 수 있어 이름은 남긴다. */
  labelKey: string
  icon: SettingsIcon
  /**
   * 이 기기에서 쓸 수 있는가. 거짓이면 **줄을 아예 만들지 않는다.**
   * 눌러도 아무 일이 없는 스위치는 고장으로 읽히고(아이폰·데스크톱의 진동이 그렇다),
   * 고장으로 읽힌 화면은 나머지 항목까지 의심받는다.
   *
   * 만들 때 한 번만 묻는다. 지금 이 값을 정하는 것들(진동 하드웨어)이 세션 도중에
   * 바뀌지 않기 때문이다.
   */
  isAvailable?: () => boolean
}>

/** 켜고 끄는 항목. 효과음·BGM·진동이 전부 이 모양이다. */
export type SettingsToggleItem = SettingsItemBase &
  Readonly<{
    kind: 'toggle'
    /** 지금 켜져 있는가. **매번 묻는다** - 상태의 원본은 각 엔진이다. */
    isOn: () => boolean
    /** 눌렸다. **바뀐 뒤의 '켜짐' 상태**를 돌려줘야 한다. */
    toggle: () => boolean
    /** 켜짐/꺼짐 접근성 문장의 사전 키. 생략하면 `{name}`을 받는 공용 문장을 쓴다. */
    ariaOnKey?: string
    ariaOffKey?: string
  }>

/**
 * 값을 고르는 항목. 언어처럼 켬/끔이 아닌 것이다.
 *
 * 값이 둘뿐이라 메뉴 없이 한 번에 넘긴다. 셋 이상이 되는 날 필요한 것은 이 타입에 목록을
 * 다는 것이 아니라 줄 하나가 하위 목록을 여는 것이고, 그때 이 파일에 층이 하나 생긴다.
 */
export type SettingsChoiceItem = SettingsItemBase &
  Readonly<{
    kind: 'choice'
    /** 지금 값의 표시 문자열(예: `한국어`). **이 항목만 값을 글자로 보여 준다.** */
    value: () => string
    /** 누르면 될 값. 접근성 문장이 "눌러서 English"를 만드는 데 쓴다. */
    nextValue: () => string
    advance: () => void
  }>

export type SettingsItem = SettingsToggleItem | SettingsChoiceItem

export type SettingsMenuOptions = Readonly<{
  mount: HTMLElement
  items: readonly SettingsItem[]
}>

export type SettingsMenuHandle = Readonly<{
  isOpen: () => boolean
  /**
   * 패널을 연다. 톱니 말고 **두 번째 손잡이**가 필요해서 생겼다(로드맵 M13 - 메뉴의 설정 카드).
   * 두 번째 설정 화면을 만들지 않는다는 규칙(M13 규칙 3)이 이 한 줄로 지켜진다 - 카드가 여는 것은
   * 같은 패널이고, 값이 사는 곳은 여전히 한 군데다.
   */
  open: () => void
  close: () => void
  /** 값이 이 패널 밖에서 바뀌었을 때 다시 물어 그린다(개발 훅 등). */
  refresh: () => void
  /** 라운드 중인가. 참이면 톱니바퀴가 흐려진다(크기는 그대로다). */
  setQuiet: (quiet: boolean) => void
  setVisible: (visible: boolean) => void
  dispose: () => void
}>

/* --- 열림 상태 ------------------------------------------------------------------ */

/**
 * 지금 설정 패널이 열려 있는가.
 *
 * 모듈 변수인 이유는 **묻는 쪽이 넷이기 때문**이다 - `app/inputLayer.ts`·`levelSelect.ts`·
 * `levelIntro.ts`·`resultScreen.ts`가 각자 창(window)에 키 핸들러를 달고 있고, 패널이
 * 떠 있는 동안 그 넷은 전부 비켜서야 한다(Esc가 패널을 닫으면서 동시에 단계 선택으로
 * 나가 버리면 그건 두 가지 일이 한 번에 일어난 것이다).
 *
 * 핸들을 넷에 전달하지 않은 이유는 소유권이다. 화면들은 설정 위젯을 만들지도 버리지도
 * 않으므로 핸들을 들고 있으면 죽은 참조를 쥐게 되고, 창 단위 키 핸들러가 필요로 하는 것은
 * 애초에 "지금 모달이 떠 있는가" 한 비트뿐이다. 패널은 앱에 하나뿐이라 세는 것으로 족하다.
 */
let openMenuCount = 0

export function isSettingsOpen(): boolean {
  return openMenuCount > 0
}

/* --- 아이콘 --------------------------------------------------------------------- */

/**
 * 아이콘 하나. 조각은 전부 CSS가 그린다(파일이 늘지 않고, 색을 토큰으로 받는다).
 *
 * 빗금은 **켬/끔이 있는 아이콘에만** 붙인다. 언어처럼 끌 수 없는 항목에 빗금 자리를 두면
 * 그 항목도 꺼질 수 있는 것처럼 보인다.
 */
function createIcon(icon: SettingsIcon): HTMLElement {
  const root = createElement('span', `gh-set__icon gh-set__icon--${icon}`)
  root.setAttribute('aria-hidden', 'true')

  switch (icon) {
    case 'sound':
      root.append(createElement('span', 'gh-set__cone'), createElement('span', 'gh-set__wave'))
      break
    case 'bgm':
      root.append(createElement('span', 'gh-set__note'), createElement('span', 'gh-set__stem'))
      break
    case 'haptics':
      root.append(createElement('span', 'gh-set__phone'), createElement('span', 'gh-set__buzz'))
      break
    case 'lang':
      root.append(createElement('span', 'gh-set__globe'), createElement('span', 'gh-set__axis'))
      return root
  }

  root.append(createElement('span', 'gh-set__slash'))
  return root
}

/* --- 줄 ------------------------------------------------------------------------- */

type Row = Readonly<{
  item: SettingsItem
  root: HTMLButtonElement
  name: HTMLElement
  /** 값 글자. 켬/끔 항목에서는 언제나 비어 있고 CSS가 자리도 주지 않는다. */
  value: HTMLElement
}>

function createRow(
  item: SettingsItem,
  onPress: (item: SettingsItem, row: HTMLButtonElement) => void,
): Row {
  const root = createElement('button', 'gh-set__row')
  root.type = 'button'
  root.dataset.item = item.id

  const name = createElement('span', 'gh-set__name', '')
  const value = createElement('span', 'gh-set__value', '')
  root.append(name, value, createIcon(item.icon))
  root.addEventListener('click', () => onPress(item, root))

  return { item, root, name, value }
}

/* --- 본체 ----------------------------------------------------------------------- */

/** 톱니바퀴 하나와 그 패널을 만들어 `mount`에 붙인다. 패널은 접힌 채로 선다. */
export function createSettingsMenu(options: SettingsMenuOptions): SettingsMenuHandle {
  /*
   * 톱니바퀴. 여덟 갈래 톱니는 CSS가 그리므로(`.gh-gear__teeth`) 여기서는 뼈대만 세운다.
   * 이 버튼의 유일한 기능은 여는 것이다 - 다시 눌러 닫는 경로도 있지만, 열려 있는 동안
   * 스크림이 이 버튼을 덮으므로 실제로 쓰이는 것은 스크림·Esc·닫기 버튼 셋이다.
   */
  const gear = createElement('button', 'gh-gear')
  gear.type = 'button'
  gear.setAttribute('aria-haspopup', 'dialog')
  const gearIcon = createElement('span', 'gh-gear__icon')
  gearIcon.setAttribute('aria-hidden', 'true')
  gearIcon.append(createElement('span', 'gh-gear__teeth'), createElement('span', 'gh-gear__hub'))
  gear.append(gearIcon)

  const overlay = createElement('div', 'gh-set')
  overlay.hidden = true

  /*
   * 스크림이 이 패널의 방어선 두 개를 한 번에 친다 - 바깥 탭으로 닫히는 경로와,
   * 캔버스로 새는 탭을 막는 경로. 뒤엣것이 중요하다: 패널 위를 쏴서 오답이 되면
   * 설정을 열었다는 이유로 탄환을 잃는다.
   */
  const scrim = createElement('div', 'gh-set__scrim')
  scrim.setAttribute('aria-hidden', 'true')

  const panel = createElement('div', 'gh-set__panel')
  panel.setAttribute('role', 'dialog')
  panel.setAttribute('aria-modal', 'true')
  const titleId = 'gh-set-title'
  panel.setAttribute('aria-labelledby', titleId)

  const head = createElement('div', 'gh-set__head')
  const title = createElement('h2', 'gh-set__title', '')
  title.id = titleId
  const closeButton = createElement('button', 'gh-set__close')
  closeButton.type = 'button'
  const closeMark = createElement('span', 'gh-set__close-mark')
  closeMark.setAttribute('aria-hidden', 'true')
  closeButton.append(closeMark)
  head.append(title, closeButton)

  const list = createElement('div', 'gh-set__list')

  panel.append(head, list)
  overlay.append(scrim, panel)
  options.mount.append(gear, overlay)

  /**
   * 실제로 세운 줄들. **지원하지 않는 항목은 여기 없다** - 만들지 않는 것이지 감추는 것이
   * 아니다. 감춘 줄은 화면 밖에서 탭 순서에 계속 잡히고, 그때 낭독기 사용자에게만
   * 존재하는 고장 난 스위치가 된다.
   */
  const rows: readonly Row[] = options.items
    .filter((item) => item.isAvailable?.() !== false)
    .map((item) => createRow(item, press))
  for (const row of rows) list.append(row.root)

  let opened = false
  /** 열기 직전에 포커스가 있던 곳. 닫을 때 돌려준다(닫힌 뒤 포커스가 body로 떨어지면 안 된다). */
  let returnFocusTo: HTMLElement | null = null

  /* --- 그리기 ----------------------------------------------------------------- */

  /**
   * 줄 하나를 지금 상태로 칠한다. **엔진에게 매번 다시 묻는다.**
   *
   * `aria-pressed`를 쓰지 않는 이유는 낭독기마다 "선택됨"의 뜻이 갈려서, 무엇이 눌린
   * 상태인지가 사람에 따라 반대로 들리기 때문이다. 대신 이름 한 문장이 상태와 다음 동작을
   * 함께 말한다("효과음 켜짐. 눌러서 끄기").
   */
  function paintRow(row: Row): void {
    const label = t(row.item.labelKey)
    setText(row.name, label)

    if (row.item.kind === 'toggle') {
      const on = row.item.isOn()
      row.root.classList.toggle('is-off', !on)
      setText(row.value, '')
      const key = on
        ? (row.item.ariaOnKey ?? 'settings.aria.on')
        : (row.item.ariaOffKey ?? 'settings.aria.off')
      setAttr(row.root, 'aria-label', t(key, { name: label }))
      return
    }

    const value = row.item.value()
    row.root.classList.remove('is-off')
    setText(row.value, value)
    setAttr(
      row.root,
      'aria-label',
      t('settings.aria.choice', { name: label, value, next: row.item.nextValue() }),
    )
  }

  function paint(): void {
    setText(title, t('settings.title'))
    setAttr(gear, 'aria-label', t('settings.open'))
    setAttr(gear, 'title', t('settings.open'))
    setAttr(closeButton, 'aria-label', t('common.close'))
    setAttr(closeButton, 'title', t('common.close'))
    for (const row of rows) paintRow(row)
  }

  /* --- 열고 닫기 --------------------------------------------------------------- */

  /** 패널 안에서 포커스를 받을 수 있는 것들. 순서가 곧 탭 순서다. */
  function focusables(): readonly HTMLButtonElement[] {
    return Array.from(panel.querySelectorAll<HTMLButtonElement>('button:not([disabled])'))
  }

  function open(): void {
    if (opened) return
    opened = true
    openMenuCount += 1

    returnFocusTo = document.activeElement instanceof HTMLElement ? document.activeElement : null

    paint()
    overlay.hidden = false
    setAttr(gear, 'aria-expanded', 'true')
    window.addEventListener('keydown', handleKeyDown)

    /*
     * **첫 줄**에 포커스를 준다. 탭 순서로는 닫기 버튼이 먼저지만(머리말이 목록보다 앞이다)
     * 이 패널을 연 사람이 하려는 일은 닫는 것이 아니다. 닫기는 Esc·바깥 탭·Shift+Tab 셋으로
     * 이미 닿으므로, 열자마자 포커스가 출구에 앉아 있을 이유가 없다.
     * 줄이 하나도 없을 수는 없지만(언어는 어느 기기에서나 있다) 그때는 닫기로 물러선다.
     */
    const first = rows[0]?.root ?? closeButton
    first.focus()
  }

  function close(): void {
    if (!opened) return
    opened = false
    openMenuCount = Math.max(0, openMenuCount - 1)

    window.removeEventListener('keydown', handleKeyDown)
    overlay.hidden = true
    setAttr(gear, 'aria-expanded', 'false')

    /*
     * 포커스를 돌려준다. 열기 전에 있던 곳이 이미 사라졌을 수 있으므로(결과 화면이 다시
     * 그려지는 등) 문서에 붙어 있는지 확인하고, 아니면 톱니바퀴로 떨어진다.
     */
    const back = returnFocusTo !== null && returnFocusTo.isConnected ? returnFocusTo : gear
    returnFocusTo = null
    back.focus()
  }

  function press(item: SettingsItem, row: HTMLButtonElement): void {
    if (item.kind === 'toggle') item.toggle()
    else item.advance()

    /*
     * 누른 줄만이 아니라 **전부** 다시 칠한다. 언어를 바꾸면 다른 줄의 이름까지 바뀌기
     * 때문이다. 줄이 넷뿐이라 아끼는 것보다 "한 줄만 옛 언어로 남는" 사고가 비싸다.
     */
    paint()

    /*
     * 포커스를 누른 줄로 되돌린다. 언어를 바꾸면 그 뒤의 화면들이 통째로 다시 세워지고
     * (`main.ts`의 `rebuildOverlays`), 그 과정에서 포커스가 **패널 밖의** 단계 줄로 끌려간다.
     * 그대로 두면 열려 있는 대화상자를 앞에 두고 포커스는 뒤에 있는 상태가 되어, 키보드로
     * 언어를 되돌릴 방법이 사라진다 - 방금 누른 버튼이 손 밑에서 없어지는 셈이다.
     */
    if (opened) row.focus()
  }

  /**
   * 패널이 열려 있는 동안의 키.
   *
   * `Escape`는 닫고, `Tab`은 패널 안을 돈다. 나머지는 손대지 않는다 - Enter·스페이스로
   * 버튼을 누르는 것은 브라우저의 일이고, 여기서 가로채면 그 기본 동작이 사라진다.
   *
   * 창에 다는 이유는 스크림을 눌러 포커스가 body로 떨어진 뒤에도 Esc가 들어야 하기
   * 때문이다. 다른 화면들의 창 핸들러와 겹치지 않는 것은 그쪽이 `isSettingsOpen()`으로
   * 스스로 비켜 주기 때문이다(파일 상단 참고).
   */
  function handleKeyDown(event: KeyboardEvent): void {
    if (!opened) return

    if (event.key === 'Escape') {
      event.preventDefault()
      close()
      return
    }

    if (event.key !== 'Tab') return

    const list = focusables()
    const first = list[0]
    const last = list[list.length - 1]
    if (first === undefined || last === undefined) return

    const active = document.activeElement
    const inside = active instanceof HTMLElement && panel.contains(active)

    if (event.shiftKey) {
      if (inside && active !== first) return
      event.preventDefault()
      last.focus()
      return
    }

    if (inside && active !== last) return
    event.preventDefault()
    first.focus()
  }

  function handleGearClick(): void {
    if (opened) close()
    else open()
  }

  gear.addEventListener('click', handleGearClick)
  closeButton.addEventListener('click', close)
  scrim.addEventListener('click', close)
  const stopLocaleWatch = onLocaleChange(paint)

  paint()

  return {
    isOpen: () => opened,
    open,
    close,
    refresh: paint,

    setQuiet(quiet: boolean): void {
      gear.classList.toggle('is-quiet', quiet)
    },

    setVisible(visible: boolean): void {
      if (!visible) close()
      // 접힌 버튼에 포커스가 남으면 키보드 탐색이 보이지 않는 것을 계속 밟는다.
      if (!visible && document.activeElement === gear) gear.blur()
      gear.hidden = !visible
    },

    dispose(): void {
      close()
      stopLocaleWatch()
      gear.removeEventListener('click', handleGearClick)
      closeButton.removeEventListener('click', close)
      scrim.removeEventListener('click', close)
      // 줄의 청취자는 화살표라 참조가 여기 없다. 노드째 버리는 것으로 정리가 끝난다.
      gear.remove()
      overlay.remove()
    },
  }
}
